import { useEffect, useMemo, useState } from 'react';
import usePagination from '../../../../hooks/usePagination';
import {
  useGetLogsAlertLogsQuery,
  useGetLogsAlertsSeverityQuery,
  useGetLogsCostByProjectQuery,
  useGetLogsHourlyQuery,
  useGetLogsRiskScoresQuery,
  useGetLogsSummaryQuery
} from '../data-access/logs.api';
import { useGetLlmAnalysisResultsQuery } from '../data-access/project.api';
import {
  buildAlertsSeverityOption,
  buildApiCallsByHourOption,
  buildCostByProjectOption,
  buildLlmAnalysisBreakdownOption,
  buildRiskScoresByHourOption,
  getAlertsControlChartHeight,
  getCostByProjectChartHeight
} from './charts';
import { CHART_DAYS, MONITORING_POLL_INTERVAL_MS, MONITORING_TABLE_PAGE_SIZE } from './constants';
import { buildKpiCards } from './formatters';
import { mapLlmBreakdownToStackedChart } from './llmAnalysis.mappers';

const monitoringPollOptions = { pollingInterval: MONITORING_POLL_INTERVAL_MS };

export function useRealtimeMonitoring() {
  const [apiLogPage, setApiLogPage] = useState(1);
  const chartRange = useMemo(() => ({ days: CHART_DAYS }), []);

  const summaryQuery = useGetLogsSummaryQuery(undefined, monitoringPollOptions);
  const hourlyQuery = useGetLogsHourlyQuery(chartRange, monitoringPollOptions);
  const riskQuery = useGetLogsRiskScoresQuery(chartRange, monitoringPollOptions);
  const costQuery = useGetLogsCostByProjectQuery(undefined, monitoringPollOptions);
  const severityQuery = useGetLogsAlertsSeverityQuery(undefined, monitoringPollOptions);

  const apiLogOffset = (apiLogPage - 1) * MONITORING_TABLE_PAGE_SIZE;
  const alertLogsQuery = useGetLogsAlertLogsQuery(
    {
      limit: MONITORING_TABLE_PAGE_SIZE,
      offset: apiLogOffset
    },
    monitoringPollOptions
  );

  const llmAnalysisQuery = useGetLlmAnalysisResultsQuery({ limit: 1, offset: 0 }, monitoringPollOptions);

  const totalApiLogs = alertLogsQuery.data?.total ?? 0;
  const totalApiLogPages = Math.max(1, Math.ceil(totalApiLogs / MONITORING_TABLE_PAGE_SIZE));
  const apiLogPagination = usePagination({
    currentPageNo: apiLogPage,
    totalPage: totalApiLogPages,
    maxPaginationButtonCount: 5
  });

  useEffect(() => {
    if (apiLogPage > totalApiLogPages) {
      setApiLogPage(totalApiLogPages);
    }
  }, [apiLogPage, totalApiLogPages]);

  const hourly = hourlyQuery.data ?? [];
  const riskScores = riskQuery.data ?? [];
  const costByProject = costQuery.data ?? [];
  const alertsSeverity = severityQuery.data ?? [];
  const alertRows = alertLogsQuery.data?.data ?? [];
  const llmBreakdownStacked = useMemo(
    () => mapLlmBreakdownToStackedChart(llmAnalysisQuery.data?.breakdown),
    [llmAnalysisQuery.data?.breakdown]
  );
  const kpis = useMemo(() => (summaryQuery.data ? buildKpiCards(summaryQuery.data) : []), [summaryQuery.data]);

  const chartOptions = useMemo(
    () => ({
      apiCalls: buildApiCallsByHourOption(hourly),
      riskScores: buildRiskScoresByHourOption(riskScores),
      cost: buildCostByProjectOption(costByProject),
      severity: buildAlertsSeverityOption(alertsSeverity),
      control: buildLlmAnalysisBreakdownOption(llmBreakdownStacked)
    }),
    [hourly, riskScores, costByProject, alertsSeverity, llmBreakdownStacked]
  );

  const alertsChartHeight = getAlertsControlChartHeight(llmBreakdownStacked?.analysisTypes.length ?? 0);
  const costChartHeight = getCostByProjectChartHeight(costByProject.length);
  const apiLogsBusy = alertLogsQuery.isLoading || alertLogsQuery.isFetching;

  return {
    summary: {
      data: summaryQuery.data,
      isLoading: summaryQuery.isLoading,
      isError: summaryQuery.isError,
      error: summaryQuery.error
    },
    kpis,
    charts: {
      options: chartOptions,
      loading: {
        hourly: hourlyQuery.isLoading,
        risk: riskQuery.isLoading,
        cost: costQuery.isLoading,
        severity: severityQuery.isLoading,
        control: llmAnalysisQuery.isLoading
      },
      alertsChartHeight,
      costChartHeight
    },
    apiLog: {
      rows: alertRows,
      page: apiLogPage,
      setPage: setApiLogPage,
      total: totalApiLogs,
      totalPages: totalApiLogPages,
      rangeStart: totalApiLogs === 0 ? 0 : apiLogOffset + 1,
      rangeEnd: Math.min(apiLogOffset + alertRows.length, totalApiLogs),
      busy: apiLogsBusy,
      isInitialLoading: alertLogsQuery.isLoading && alertRows.length === 0,
      pagination: apiLogPagination
    }
  };
}
